import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { Subject } from "rxjs";
import { SessionService } from "./session.service";


/**
 * @service FirebaseNotificationsMessagingService
 * @description
 * Service responsible for handling Firebase Cloud Messaging (FCM) push notifications.
 * It manages the lifecycle of notification permissions, token retrieval, and listening for incoming messages.
 *
 * ## Key Responsibilities:
 * 1. **Permission Management**: Requests user permission to show notifications in the browser.
 * 2. **Token Management**: Retrieves the FCM registration token and syncs it with the user's session on the backend.
 * 3. **Message Listening**: Listens for foreground push notifications and triggers UI updates.
 * 4. **Notification Broadcasting**: Exposes a Subject to notify other components (like TopNav) when a new notification arrives.
 *
 * ## Dependencies:
 * - `SessionService`: To update the user's session with the new FCM token.
 * - Firebase SDK (`getMessaging`, `getToken`, `onMessage`): For direct interaction with Firebase.
 *
 * @service-provider root (implied by usage, though @Injectable() doesn't specify providedIn here, it is likely provided in a module or root).
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable()
export class FirebaseNotificationsMessagingService {

  /**
   * @type {Subject<void>}
   * @description
   * RxJS Subject used to broadcast events when a new push notification is received.
   * Components can subscribe to this to update notification counters or lists in real-time.
   *
   * @example
   * this.firebaseMessagingService.firebaseNotificationObject.subscribe(() => {
   *   this.refreshNotifications();
   * });
   */
  public firebaseNotificationObject = new Subject<void>();

  /**
   * @method constructor
   * @description Initializes the service with required dependencies.
   *
   * @param {SessionService} sessinService - Service for managing user sessions and updating tokens.
   */
  constructor(private sessinService: SessionService) {

  }

  /**
   * @method ngOninit
   * @lifecycle
   * @description Lifecycle hook placeholder. Currently empty.
   */
  public ngOninit() {

  }

  /**
   * @method requestNotificationPermission
   * @description
   * Requests permission from the user to display browser notifications.
   * If permission is granted and a user session exists, it retrieves the FCM token
   * and updates the backend session.
   *
   * ## Flow:
   * 1. Checks if the browser supports Notifications.
   * 2. Calls `Notification.requestPermission()`.
   * 3. If granted:
   *    - Checks `localStorage` for active session (`isSessionExist`).
   *    - Calls `getToken` with VAPID key from environment.
   *    - Stores token in `localStorage` ('firebaseToken').
   *    - Calls `sessinService.sessionUpdate` to save token on server.
   *
   * @returns {void}
   * @side-effect
   * - Prompts user for permission.
   * - Writes to `localStorage`.
   * - Makes HTTP request via `SessionService`.
   */
  public requestNotificationPermission() {

    if (!('Notification' in window)) {
      alert('Notifications are not supported by this browser.');
      return;
    }

    Notification.requestPermission().then((permission) => {
      if (permission == 'granted') {
        if (localStorage.getItem('isSessionExist') == 'yes') {
          const messaging = getMessaging();
          getToken(messaging, { vapidKey: environment.firebase.vapidKey })
              .then((newInstanceIdToken) => {
                  if (newInstanceIdToken) {
                    localStorage.setItem('firebaseToken', newInstanceIdToken);
                    this.sessinService.sessionUpdate(localStorage.getItem('userID'), newInstanceIdToken)
                  } else {

                  }
              })
              .catch(() => {
                console.error('An error occurred while retrieving token.');
              });
        }
      } else {

      }
    })
  }

  /**
   * @method listenPushNotifications
   * @description
   * Initializes the listener for incoming FCM messages when the application is in the foreground.
   *
   * ## Flow:
   * 1. Gets messaging instance.
   * 2. Subscribes to `onMessage`.
   * 3. On message receipt:
   *    - Triggers `firebaseNotificationObject` to notify app components.
   *    - Retrieves the Service Worker registration.
   *    - Manually shows the notification using `registration.showNotification` (useful for foreground notifications).
   *
   * @returns {void}
   * @side-effect
   * - Emits value to `firebaseNotificationObject`.
   * - Displays browser notification.
   */
  public listenPushNotifications() {
      const messaging = getMessaging();
      onMessage(messaging, (payload) => {
        this.firebaseNotificationObject.next();
        navigator.serviceWorker.getRegistration('/firebase-cloud-messaging-push-scope').then(registration => {
          registration.showNotification(payload['notification'].title, {
            body: payload['notification'].body
          });
        });
      });
  }

  /**
   * @method notifyUpdate
   * @description
   * Manually triggers the notification update subject.
   * Can be used to force a refresh of notification UI components.
   *
   * @returns {void}
   * @side-effect Emits value to `firebaseNotificationObject`.
   */
  public notifyUpdate() {
    this.firebaseNotificationObject.next();
  }
}
